import { computed, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { User } from '@/api/types'
import { useAuthState } from './useAuthState'

export interface NavItem {
  id: string
  path: string
  icon: string
  label: string
  badge?: number | null
  to?: string
  onClick?: () => void
}

// Common nav items to avoid repetition
const NAV_ITEMS = {
  home: { id: 'home', path: '/', icon: '🏠', label: 'Home' },
  login: { id: 'login', path: '/login', icon: '🔑', label: 'Sign In' },
  register: {
    id: 'register',
    path: '/register',
    icon: '📝',
    label: 'Register',
  },
  dashboard: {
    id: 'dashboard',
    path: '/dashboard',
    icon: '📊',
    label: 'Dashboard',
  },
  bookings: {
    id: 'bookings',
    path: '/bookings',
    icon: '📅',
    label: 'Bookings',
  },
  locations: {
    id: 'locations',
    path: '/locations',
    icon: '📍',
    label: 'Locations',
  },
  settings: {
    id: 'settings',
    path: '/settings',
    icon: '⚙️',
    label: 'Settings',
  },
  jobs: { id: 'jobs', path: '/jobs', icon: '📋', label: 'Jobs' },
  customers: {
    id: 'customers',
    path: '/admin/customers',
    icon: '👥',
    label: 'Customers',
  },
  employees: {
    id: 'employees',
    path: '/admin/employees',
    icon: '👔',
    label: 'Employees',
  },
} as const satisfies Record<string, NavItem>

// Helper for lazy initialization to avoid injection errors during app init
function lazyInit<T>(initFn: () => T): () => T | null {
  let instance: T | null = null
  return () => {
    if (!instance) {
      try {
        instance = initFn()
      } catch {
        // Not available yet
      }
    }
    return instance
  }
}

export function useNavigation(user: Ref<User | null>) {
  const getRoute = lazyInit(useRoute)
  const getRouter = lazyInit(useRouter)

  const { isCustomer, isEmployee, isServiceProvider, isSuperAdmin } =
    useAuthState()

  /**
   * Get navigation items based on user type and role
   */
  const getNavItems = computed((): NavItem[] => {
    // Unauthenticated navigation
    if (!user.value) {
      return [NAV_ITEMS.home, NAV_ITEMS.login, NAV_ITEMS.register]
    }

    // Customer navigation
    if (isCustomer.value) {
      return [
        NAV_ITEMS.dashboard,
        NAV_ITEMS.bookings,
        NAV_ITEMS.locations,
        NAV_ITEMS.settings,
      ]
    }

    // Service Provider navigation
    if (isEmployee.value && isServiceProvider.value) {
      return [NAV_ITEMS.dashboard, NAV_ITEMS.jobs, NAV_ITEMS.settings]
    }

    // Employee (non-service-provider) navigation
    if (isEmployee.value) {
      const navItems: NavItem[] = [
        NAV_ITEMS.dashboard,
        NAV_ITEMS.bookings,
        NAV_ITEMS.customers,
      ]

      // Only super admins see employees
      if (isSuperAdmin.value) {
        navItems.push(NAV_ITEMS.employees)
      }

      // All admins (manager + super) see locations and settings
      navItems.push(NAV_ITEMS.locations, NAV_ITEMS.settings)

      return navItems
    }

    return []
  })

  /**
   * Check if the given path is the currently active route
   */
  const isActive = (path: string): boolean => {
    const currentRoute = getRoute()
    return currentRoute ? currentRoute.path === path : false
  }

  /**
   * Navigate to the specified path
   */
  const navigateTo = async (path: string): Promise<void> => {
    const currentRouter = getRouter()
    if (currentRouter) {
      await currentRouter.push(path)
    }
  }

  return {
    getNavItems,
    isActive,
    navigateTo,
  }
}
